import { useEffect, useRef } from 'react';
import type { MapLayerMouseEvent, MapMouseEvent } from 'maplibre-gl';
import { mapEngine } from '../../map-engine/MapEngineService';

const SOURCE_ID = 'canonical-network';
const EDGES_LAYER_ID = 'canonical-network-edges';
const NODES_LAYER_ID = 'canonical-network-nodes';

export interface AssociationMapTarget {
  type: 'edge' | 'node';
  id: string;
}

interface UseAssociationMapSelectionConfig {
  enabled: boolean;
  allowNodes?: boolean;
  onPick: (target: AssociationMapTarget) => void;
  onInvalidPick?: () => void;
  selectedTarget?: AssociationMapTarget | null;
}

function getFeatureId(feature: { id?: string | number; properties?: Record<string, unknown> }): string | null {
  if (feature.id !== undefined && feature.id !== null) {
    return String(feature.id);
  }

  const propertyId = feature.properties?.id;
  return typeof propertyId === 'string' || typeof propertyId === 'number'
    ? String(propertyId)
    : null;
}

export function useAssociationMapSelection({
  enabled,
  allowNodes = true,
  onPick,
  onInvalidPick,
  selectedTarget,
}: UseAssociationMapSelectionConfig): void {
  const selectedRef = useRef<AssociationMapTarget | null>(null);
  const hoveredRef = useRef<AssociationMapTarget | null>(null);

  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map || !enabled) {
      return;
    }

    const clearHovered = (): void => {
      if (!hoveredRef.current) return;
      map.removeFeatureState(
        { source: SOURCE_ID, id: hoveredRef.current.id },
        'hover',
      );
      hoveredRef.current = null;
    };

    const clearSelected = (): void => {
      if (!selectedRef.current) return;
      map.removeFeatureState(
        { source: SOURCE_ID, id: selectedRef.current.id },
        'selected',
      );
      selectedRef.current = null;
    };

    const applySelected = (target: AssociationMapTarget | null): void => {
      clearSelected();
      if (!target) return;
      map.setFeatureState(
        { source: SOURCE_ID, id: target.id },
        { selected: true },
      );
      selectedRef.current = target;
    };

    const resolveTarget = (event: MapMouseEvent): AssociationMapTarget | null => {
      const features = map.queryRenderedFeatures(event.point, {
        layers: allowNodes ? [EDGES_LAYER_ID, NODES_LAYER_ID] : [EDGES_LAYER_ID],
      });

      if (!features.length) {
        return null;
      }

      const feature = features[0];
      const id = getFeatureId(feature);
      if (!id) return null;

      return {
        type: feature.layer.id === NODES_LAYER_ID ? 'node' : 'edge',
        id,
      };
    };

    const handleMouseMove = (event: MapMouseEvent): void => {
      const target = resolveTarget(event);

      if (!target) {
        clearHovered();
        if (map.getCanvas().style.cursor === 'pointer') {
          map.getCanvas().style.cursor = '';
        }
        return;
      }

      if (
        hoveredRef.current &&
        hoveredRef.current.id === target.id &&
        hoveredRef.current.type === target.type
      ) {
        return;
      }

      clearHovered();
      map.setFeatureState(
        { source: SOURCE_ID, id: target.id },
        { hover: true },
      );
      hoveredRef.current = target;
      map.getCanvas().style.cursor = 'pointer';
    };

    const handleMouseLeave = (): void => {
      clearHovered();
      if (map.getCanvas().style.cursor === 'pointer') {
        map.getCanvas().style.cursor = '';
      }
    };

    const handleClick = (event: MapLayerMouseEvent | MapMouseEvent): void => {
      const target = resolveTarget(event as MapMouseEvent);

      if (!target) {
        onInvalidPick?.();
        return;
      }

      event.preventDefault();
      event.originalEvent.stopPropagation();
      applySelected(target);
      onPick(target);
    };

    map.on('mousemove', handleMouseMove);
    map.on('mouseleave', EDGES_LAYER_ID, handleMouseLeave);
    if (allowNodes) {
      map.on('mouseleave', NODES_LAYER_ID, handleMouseLeave);
    }
    map.on('click', handleClick);

    applySelected(selectedTarget ?? null);

    return () => {
      map.off('mousemove', handleMouseMove);
      map.off('mouseleave', EDGES_LAYER_ID, handleMouseLeave);
      if (allowNodes) {
        map.off('mouseleave', NODES_LAYER_ID, handleMouseLeave);
      }
      map.off('click', handleClick);
      clearHovered();
      clearSelected();
      if (map.getCanvas().style.cursor === 'pointer') {
        map.getCanvas().style.cursor = '';
      }
    };
  }, [allowNodes, enabled, onInvalidPick, onPick, selectedTarget]);
}
